import React, { ChangeEvent, FocusEvent, useRef, useState } from "react";
import { OptionDropDown, DropdownOption } from "./optionDropDown";
import { Eye, EyeOff } from "lucide-react";

interface TextInputBoxProps {
  id: string;
  label?: string;
  placeHolder?: string;
  placeholder?: string;
  inputValue?: string;
  value?: string;
  maxLength?: number;
  onInputChange?: (value: string) => void;
  onChange?: (value: string) => void;
  onFocusChange?: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  type?: "text" | "password" | "email" | "tel" | "url";
  disabled?: boolean;
  required?: boolean;
  dropDownOptions?: DropdownOption[];
  error?: boolean | string;
  className?: string;
  size?: "sm" | "md" | "lg";
  multiline?: boolean;
  rows?: number;
}

interface TextInputBoxLargeProps extends Omit<TextInputBoxProps, "dropDownOptions" | "multiline"> {
  dropDownOptions?: never;
  multiline?: never;
  rows?: number;
}

const TextInputBox: React.FC<TextInputBoxProps> = ({
  id,
  label,
  placeHolder,
  placeholder,
  inputValue,
  value,
  maxLength,
  onInputChange,
  onChange,
  onFocusChange,
  onFocus,
  onBlur,
  type = "text",
  disabled = false,
  required = false,
  dropDownOptions,
  error,
  className = "",
  size = "md",
  multiline = false,
  rows = 3
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Unified value handling - support both prop patterns
  const currentValue = inputValue ?? value ?? "";
  const currentPlaceholder = placeHolder ?? placeholder ?? "";
  const currentOnChange = onInputChange ?? onChange;
  const hasError = typeof error === "string" ? !!error : !!error;
  const errorMessage = typeof error === "string" ? error : undefined;

  // Determine the actual input type
  const inputType = type === "password" && showPassword ? "text" : type;

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.preventDefault();
    const { value } = e.target;
    currentOnChange?.(value);
  };

  const handleFocus = (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setIsFocused(true);
    onFocusChange?.(e);
    onFocus?.(e);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setIsFocused(false);
    onFocusChange?.(e);
    onBlur?.(e);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const getSizeClasses = () => {
    switch (size) {
      case "sm":
        return "px-3 py-2 text-sm";
      case "lg":
        return "px-4 py-3 text-lg";
      default:
        return "px-4 py-3 text-sm";
    }
  };

  const baseClasses = `
    w-full font-medium text-secondary-900 bg-white border rounded-lg
    transition-all duration-200 ease-in-out placeholder-secondary-400
    focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
    disabled:bg-secondary-50 disabled:text-secondary-500 disabled:cursor-not-allowed
    ${getSizeClasses()}
    ${
      hasError
        ? "border-error-300 focus:ring-error-500 focus:border-error-500"
        : "border-secondary-300 hover:border-secondary-400"
    }
    ${isFocused ? "shadow-lg" : "shadow-sm"}
    ${className}
  `
    .trim()
    .replace(/\s+/g, " ");

  const inputProps = {
    id,
    name: currentPlaceholder,
    value: currentValue,
    onChange: handleInputChange,
    onFocus: handleFocus,
    onBlur: handleBlur,
    placeholder: currentPlaceholder,
    disabled,
    maxLength: maxLength ?? (multiline ? 500 : 80),
    required,
    className: baseClasses
  };

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-secondary-700 mb-2">
          {label}
          {required && <span className="text-error-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative" ref={containerRef}>
        {multiline ? (
          <textarea {...inputProps} rows={rows} style={{ resize: "vertical" }} />
        ) : (
          <input {...inputProps} type={inputType} />
        )}

        {type === "password" && !multiline && (
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600 focus:outline-none focus:text-secondary-600 transition-colors duration-200 p-2 bg-transparent"
            tabIndex={-1}
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        )}

        {dropDownOptions && (
          <OptionDropDown
            value={currentValue}
            onChange={(value) => {
              currentOnChange?.(value);
              setIsFocused(false); // Close dropdown after selection
            }}
            isOpen={isFocused}
            options={dropDownOptions}
            triggerRef={containerRef}
          />
        )}
      </div>

      {errorMessage && <p className="mt-1 text-sm text-error-600">{errorMessage}</p>}
    </div>
  );
};

const TextInputBoxLarge: React.FC<TextInputBoxLargeProps> = ({ rows = 4, ...props }) => {
  return <TextInputBox {...props} multiline={true} rows={rows} />;
};

export { TextInputBox, TextInputBoxLarge };
